import { useEffect, useId, useRef } from "react";
import type { InputHTMLAttributes, Ref, TextareaHTMLAttributes } from "react";
import type { Option } from "../engine/types";
import { Icon } from "./Icon";

/**
 * M3 form controls.
 *
 * Deliberate deviation from the spec: the label sits ABOVE the field instead of
 * floating inside it. Our labels are whole questions in Arabic ("شنو نوع النص؟")
 * — M3's floating label assumes a two-word field name and truncates anything
 * longer. The questions are the product, so they get a real line of their own.
 * Everything else (outline, supporting text, error state, counter) is spec.
 */

const OUTLINE_BASE =
  "w-full rounded-xs border bg-surface-lowest px-4 text-on-surface md-body-large " +
  "transition-[border-color,box-shadow] outline-none placeholder:text-on-surface-variant/60";

function outlineState(invalid: boolean): string {
  return invalid
    ? "border-error focus:border-error focus:ring-1 focus:ring-error"
    : "border-outline hover:border-on-surface focus:border-primary focus:ring-1 focus:ring-primary";
}

// ─────────────────────────── Text ───────────────────────────

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  invalid?: boolean;
  /** React 19 passes ref as an ordinary prop — declaring it is what makes
   *  `<TextInput ref={…}>` actually reach the DOM node (the OTP field focuses
   *  itself this way). Without this line the ref is silently dropped. */
  ref?: Ref<HTMLInputElement>;
}

export function TextInput({ invalid = false, className = "", ref, ...rest }: TextInputProps) {
  return (
    <input
      {...rest}
      ref={ref}
      aria-invalid={invalid || undefined}
      className={`${OUTLINE_BASE} ${outlineState(invalid)} min-h-[3.5rem] ${className}`}
    />
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  minRows?: number;
  ref?: Ref<HTMLTextAreaElement>;
}

export function TextArea({
  invalid = false,
  minRows = 5,
  className = "",
  value,
  ref,
  ...rest
}: TextAreaProps) {
  const innerRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow: a 5000-word paste shouldn't live in a 5-line porthole.
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 560)}px`;
  }, [value]);

  // Keep the caller's ref working without giving up the auto-grow measurement.
  const setRefs = (el: HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  return (
    <textarea
      {...rest}
      ref={setRefs}
      value={value}
      rows={minRows}
      aria-invalid={invalid || undefined}
      className={`${OUTLINE_BASE} ${outlineState(invalid)} resize-y py-4 leading-[1.8] ${className}`}
    />
  );
}

// ─────────────────────────── Select ───────────────────────────

export function SelectInput({
  value,
  options,
  onChange,
  invalid = false,
  id,
  describedBy,
  disabled,
}: {
  value: string;
  options: Option[];
  onChange: (v: string) => void;
  invalid?: boolean;
  id?: string;
  describedBy?: string;
  disabled?: boolean;
}) {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        className={`${OUTLINE_BASE} ${outlineState(invalid)} min-h-[3.5rem] cursor-pointer appearance-none pe-11 disabled:cursor-not-allowed disabled:opacity-[0.38]`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon
        name="arrow_drop_down"
        size={24}
        className="pointer-events-none absolute inset-y-0 end-3 my-auto h-6 text-on-surface-variant"
      />
    </div>
  );
}

/**
 * Selected-option hints. In a native <select> the per-option hint can't render
 * in the list, so it renders under the field for whatever is currently chosen —
 * the teaching still lands, just one beat later.
 */
export function SelectedOptionHint({ options, value }: { options: Option[]; value: string }) {
  const hint = options.find((o) => o.value === value)?.hint;
  if (!hint) return null;
  return (
    <p className="md-body-small mt-2 flex items-start gap-1.5 text-on-surface-variant">
      <Icon name="subdirectory_arrow_left" size={14} className="mt-1 shrink-0 opacity-70" />
      <span>{hint}</span>
    </p>
  );
}

// ─────────────────────────── Radio / checkbox cards ───────────────────────────

/**
 * Options render as cards, not bare rows. `Option.hint` is the reason this
 * product beats a chat box — it explains WHEN to pick a thing — so it gets a
 * full line at body-small next to the label, never 10px grey at the margin.
 */
function OptionCard({
  selected,
  invalid,
  children,
  onClick,
  role,
  ...aria
}: {
  selected: boolean;
  invalid: boolean;
  children: React.ReactNode;
  onClick: () => void;
  role: "radio" | "checkbox";
  "aria-checked": boolean;
  "aria-labelledby": string;
  "aria-describedby"?: string;
}) {
  return (
    <div
      {...aria}
      role={role}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onClick();
        }
      }}
      className={[
        "md-state flex cursor-pointer items-start gap-3 rounded-sm border p-3 text-start transition-colors",
        selected
          ? "border-primary bg-primary-container/40"
          : invalid
            ? "border-error bg-surface-lowest"
            : "border-outline-variant bg-surface-lowest hover:border-outline",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  invalid = false,
  labelledBy,
  describedBy,
}: {
  name: string;
  options: Option[];
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  labelledBy: string;
  describedBy?: string;
}) {
  const uid = useId();
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className="grid gap-2"
    >
      {options.map((o) => {
        const selected = value === o.value;
        const labelId = `${uid}-${name}-${o.value}-l`;
        const hintId = o.hint ? `${uid}-${name}-${o.value}-h` : undefined;
        return (
          <OptionCard
            key={o.value}
            role="radio"
            selected={selected}
            invalid={invalid}
            onClick={() => onChange(o.value)}
            aria-checked={selected}
            aria-labelledby={labelId}
            aria-describedby={hintId}
          >
            <span
              className={[
                "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition-colors",
                selected ? "border-primary" : "border-on-surface-variant",
              ].join(" ")}
            >
              {selected ? <span className="h-2.5 w-2.5 rounded-full bg-primary" /> : null}
            </span>
            <span className="min-w-0">
              <span id={labelId} className="md-body-large block text-on-surface">
                {o.label}
              </span>
              {o.hint ? (
                <span id={hintId} className="md-body-small mt-0.5 block text-on-surface-variant">
                  {o.hint}
                </span>
              ) : null}
            </span>
          </OptionCard>
        );
      })}
    </div>
  );
}

export function CheckboxGroup({
  name,
  options,
  value,
  onChange,
  invalid = false,
  labelledBy,
  describedBy,
}: {
  name: string;
  options: Option[];
  value: string[];
  onChange: (v: string[]) => void;
  invalid?: boolean;
  labelledBy: string;
  describedBy?: string;
}) {
  const uid = useId();
  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }
  return (
    <div
      role="group"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      className="grid gap-2"
    >
      {options.map((o) => {
        const selected = value.includes(o.value);
        const labelId = `${uid}-${name}-${o.value}-l`;
        const hintId = o.hint ? `${uid}-${name}-${o.value}-h` : undefined;
        return (
          <OptionCard
            key={o.value}
            role="checkbox"
            selected={selected}
            invalid={invalid}
            onClick={() => toggle(o.value)}
            aria-checked={selected}
            aria-labelledby={labelId}
            aria-describedby={hintId}
          >
            <span
              className={[
                "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-xs border-2 transition-colors",
                selected ? "border-primary bg-primary text-on-primary" : "border-on-surface-variant",
              ].join(" ")}
            >
              {selected ? <Icon name="check" size={14} weight={700} /> : null}
            </span>
            <span className="min-w-0">
              <span id={labelId} className="md-body-large block text-on-surface">
                {o.label}
              </span>
              {o.hint ? (
                <span id={hintId} className="md-body-small mt-0.5 block text-on-surface-variant">
                  {o.hint}
                </span>
              ) : null}
            </span>
          </OptionCard>
        );
      })}
    </div>
  );
}

// ─────────────────────────── Switch ───────────────────────────

export function Switch({
  checked,
  onChange,
  labelledBy,
  describedBy,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  labelledBy: string;
  describedBy?: string;
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onClick={() => onChange(!checked)}
      className={[
        "relative inline-flex h-8 w-[3.25rem] shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors",
        checked ? "border-primary bg-primary" : "border-outline bg-surface-highest",
      ].join(" ")}
    >
      {/* Positioned with insetInlineStart, so the thumb travels start→end and
          the whole control mirrors correctly under dir="rtl" for free. */}
      <span
        className={[
          "absolute grid place-items-center rounded-full",
          "transition-[inset-inline-start,width,height,background-color] duration-150 ease-out",
          checked ? "h-6 w-6 bg-on-primary text-primary" : "h-4 w-4 bg-outline text-transparent",
        ].join(" ")}
        style={{ insetInlineStart: checked ? "1.5rem" : "0.375rem" }}
      >
        {checked ? <Icon name="check" size={14} weight={700} /> : null}
      </span>
    </button>
  );
}

// ─────────────────────────── File ───────────────────────────

export function FileInput({
  id,
  accept,
  fileName,
  onFile,
  invalid = false,
  describedBy,
}: {
  id: string;
  accept?: string[];
  fileName?: string;
  onFile: (file: File | null) => void;
  invalid?: boolean;
  describedBy?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div>
      <input
        ref={ref}
        id={id}
        type="file"
        className="sr-only"
        accept={accept?.join(",")}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
      <div
        className={[
          "flex items-center gap-3 rounded-xs border border-dashed p-4",
          invalid ? "border-error" : "border-outline",
        ].join(" ")}
      >
        <Icon
          name={fileName ? "description" : "upload_file"}
          size={24}
          className="shrink-0 text-on-surface-variant"
        />
        <span className="md-body-medium min-w-0 flex-1 truncate text-on-surface-variant">
          {fileName ?? "ما اخترت ملف بعد"}
        </span>
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="md-state md-label-large shrink-0 cursor-pointer rounded-full border border-outline px-4 py-2 text-primary"
        >
          {fileName ? "بدّل" : "اختر ملف"}
        </button>
        {fileName ? (
          <button
            type="button"
            onClick={() => {
              if (ref.current) ref.current.value = "";
              onFile(null);
            }}
            aria-label="إزالة الملف"
            className="md-state grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-full text-on-surface-variant"
          >
            <Icon name="close" size={18} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
